import Actions from "@/elements/Actions";
import { isActiveOrOpen } from "@/lib/orders";
import { cn, putComma } from "@/lib/utils";
import { useRouter } from "next/router";
import React, { useEffect } from "react";
import { MdDisplaySettings, MdInsertLink } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import gttIcon from "@/assets/icons/gttIcon.svg";
import Image from "next/image";
import { resetStocksDetails } from "@/store/features/stocksDetailsSlice";
import { setOpenGTTBfSlice } from "@/store/features/gttBfSlice";
import ProfitOrLossWithoutPerc from "@/elements/ProfitOrLoss/ProfitOrLossWithoutPerc";
import event_white from "@/assets/icons/event_upcoming_white.svg";
import { Mixpanel } from "@/elements/Mixpanel";
import { getForGttOrderStatus, getForRegularOrderStatus } from "@/lib/placeOrder";
import { getInstrument } from "@/lib/tradeCards";
import ProfitOrLoss from "@/elements/ProfitOrLoss/ProfitOrLoss";
import { getPositionGain } from "@/lib/positions";

export default function StockDetailsMain({ trade }) {
    const router = useRouter();
    const dispatch = useDispatch();
    const { data, orderDetails, gttDetails, positionsDetails, holdingDetails, isOderDetails } = useSelector(
        (state) => state.stocksDetails
    );
    const { orderNoWithGtt } = useSelector((state) => state.orders);
    const isGtt = (orderNo) => {
        if (orderNo) {
            return orderNoWithGtt?.includes(orderNo.toString()) ?? false;
        }
        return false;
    };

    const details = orderDetails?.status
        ? orderDetails
        : gttDetails?.status
        ? gttDetails
        : holdingDetails?.finCode
        ? holdingDetails
        : positionsDetails;

    useEffect(() => {
        if (positionsDetails?.symbol) {
            Mixpanel.pageView({
                page: "positions_bf",
                tab: "positions",
                symbol: positionsDetails?.symbol,
                instrument_name: getInstrument(positionsDetails?.symbol),
                is_position_open: positionsDetails?.open ? "yes" : "no",
                is_gtt: positionsDetails?.hasGtt ? "yes" : "no",
                is_tradeidea: positionsDetails?.tradeCardId ? "yes" : "no",
                expiry_date: null,
                product: positionsDetails?.productTag
            });
        }
    }, [positionsDetails]);

    return (
        <div className="font-Inter  mx-4">
            {orderDetails?.status && (
                <div className="py-3 flex justify-between gap-4 px-4 border border-[#9D9D9D] rounded-2xl">
                    <div className="flex flex-col justify-center text-[#747474] font-medium text-xs ">
                        Type
                        <div className="text-black text-xs leading-5 font-semibold">
                            {orderDetails?.order_type ?? "-"}
                        </div>
                    </div>
                    {isActiveOrOpen(orderDetails, null) && (
                        <>
                            <div className="flex flex-col justify-center text-[#747474] font-medium text-xs ">
                                Price
                                <div className="text-black text-xs leading-5 font-semibold">
                                    {orderDetails?.price ?? "-"}
                                </div>
                            </div>
                            {!!orderDetails?.trigger_price && (
                                <div className="flex flex-col justify-center text-[#747474] font-medium text-xs ">
                                    Trgr. price
                                    <div className="text-black text-xs leading-5 font-semibold">
                                        {orderDetails?.trigger_price ?? "-"}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    {!isActiveOrOpen(orderDetails, null) && (
                        <>
                            <div className="flex flex-col justify-center text-[#747474] font-medium text-xs ">
                                Avg. price
                                <div className="text-black text-xs leading-5 font-semibold">
                                    {putComma(
                                        getForRegularOrderStatus(orderDetails?.status)[1]?.includes("EXECUTED")
                                            ? orderDetails?.traded_price
                                            : orderDetails?.price
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                    <div className="flex flex-col justify-center text-[#747474] font-medium text-xs ">
                        Filled qty.
                        <div className="text-black text-xs leading-5 font-semibold">
                            {orderDetails?.traded_qty ?? 0}/{orderDetails?.total_quantity ?? "-"}
                        </div>
                    </div>
                </div>
            )}
            {isGtt(orderDetails?.order_no) && (
                <>
                    <div
                        onClick={() => {
                            dispatch(resetStocksDetails());
                            router.push(`/user/orders/gtt?symbol=${orderDetails?.symbol}`);
                            Mixpanel.track("cta_clicked", {
                                page: orderDetails?.status
                                    ? "order_regular_bf"
                                    : gttDetails?.status
                                    ? "order_gtt_bf"
                                    : "positions_info_bff",
                                filter_chip: orderDetails?.status
                                    ? getForRegularOrderStatus(orderDetails?.status)[0]
                                    : gttDetails?.status
                                    ? getForGttOrderStatus(gttDetails?.status)[0]
                                    : positionsDetails.open
                                    ? "open"
                                    : "closed",
                                product_type:
                                    details?.product == "I"
                                        ? "intraday"
                                        : details?.product == "M"
                                        ? "overnight"
                                        : "delivery",
                                cta_text: "view_gtt",
                                symbol: details?.symbol,
                                expiry_date: null,
                                instrument_name: getInstrument(details?.symbol),
                                order_status: details?.status,
                                is_tradeidea: details?.trade_card_id ? true : false,
                                is_position_active: null,
                                is_watchlisted: null,
                                is_gtt: true
                            });
                        }}
                        className="cursor-pointer mt-4 mx-auto h-fit self-center w-fit flex items-center gap-2 border border-[#0862BC] px-[12px] rounded-[20px] py-[1px] border-solid"
                    >
                        <p className="text-[#0862BC] text-center text-xs font-bold">
                            View active <span className='[font-family:"Kaushan_Script"] '>GTT</span>
                        </p>
                        <Image className="w-[12px]" src={gttIcon} alt="gttIcon" />
                    </div>
                </>
            )}
            {gttDetails?.status && (
                <div className="py-3 flex justify-between gap-4 px-4 border border-[#9D9D9D] rounded-2xl">
                    <div className="flex flex-col justify-center text-[#747474] font-medium text-xs ">
                        Type
                        <div className="text-black text-xs leading-5 font-semibold">
                            {gttDetails?.gtt_type === "O" ? "OCO" : "Single"}
                        </div>
                    </div>

                    {gttDetails?.stop_loss && (
                        <div className="flex flex-col justify-center text-[#747474] font-medium text-xs ">
                            Stop loss
                            <div className="text-black text-xs leading-5 font-semibold">
                                ₹{putComma(gttDetails?.stop_loss)}
                            </div>
                        </div>
                    )}

                    {gttDetails?.price && (
                        <div className="flex flex-col justify-center text-[#747474] font-medium text-xs ">
                            {gttDetails?.gtt_type === "O" ? "Target price" : "Price"}
                            <div className="text-black text-xs leading-5 font-semibold">
                                ₹{putComma(gttDetails?.price)}
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col justify-center text-[#747474] font-medium text-xs ">
                        Qty.
                        <div className="text-black text-xs leading-5 font-semibold text-right">
                            {gttDetails?.total_quantity ?? "-"}
                        </div>
                    </div>
                </div>
            )}
            {positionsDetails?.productType && (
                <div className="py-3 flex justify-between gap-4 px-4 border border-[#9D9D9D] rounded-2xl">
                    <div className="flex flex-col justify-center text-[#747474] font-medium text-xs ">
                        Net qty.
                        <div className="text-black text-xs leading-5 font-semibold">{positionsDetails?.qty ?? "-"}</div>
                    </div>
                    <div className="flex flex-col justify-center text-[#747474] font-medium text-xs ">
                        Day’s P&L
                        <div
                            className={cn("text-black text-xs leading-5 font-semibold", {
                                "text-[#26A649]": getPositionGain(positionsDetails, trade?.ltp)?.dayGain >= 0,
                                "text-[#EB4E2C]": getPositionGain(positionsDetails, trade?.ltp)?.dayGain < 0
                            })}
                        >
                            {putComma(getPositionGain(positionsDetails, trade?.ltp)?.dayGain)}
                        </div>
                    </div>
                    <div className="flex flex-col justify-center text-[#747474] font-medium text-xs ">
                        Total P&L
                        <div
                            className={cn("text-black text-xs leading-5 font-semibold", {
                                "text-[#26A649]": getPositionGain(positionsDetails, trade?.ltp)?.overallGain >= 0,
                                "text-[#EB4E2C]": getPositionGain(positionsDetails, trade?.ltp)?.overallGain < 0
                            })}
                        >
                            {putComma(getPositionGain(positionsDetails, trade?.ltp)?.overallGain)}
                        </div>
                    </div>
                </div>
            )}
            {holdingDetails?.finCode && (
                <div className="py-3 flex justify-between gap-4 px-4 border border-[#9D9D9D] rounded-2xl">
                    <div className="flex flex-col justify-center text-[#747474] font-medium text-xs ">
                        Invested value
                        <div className="text-black text-xs leading-5 font-semibold">
                            {holdingDetails?.cost_price * holdingDetails?.quantity
                                ? "₹" + putComma(holdingDetails?.cost_price * holdingDetails?.quantity)
                                : "-"}
                        </div>
                    </div>
                    <div className="flex flex-col justify-center text-[#747474] font-medium text-xs ">
                        Current value
                        <div className="text-black text-xs leading-5 font-semibold">
                            {trade?.ltp ? "₹" + putComma(trade?.ltp * holdingDetails?.quantity) : "-"}
                        </div>
                    </div>
                    <div className="flex flex-col justify-center text-[#747474] font-medium text-xs ">
                        Unrealized P&L
                        <ProfitOrLossWithoutPerc
                            nseLtpPrice={trade?.ltp}
                            nseClosePrice={holdingDetails?.cost_price}
                            quantity={holdingDetails?.quantity}
                            textClass="text-sm font-medium leading-6"
                        />
                    </div>
                </div>
            )}
            {((positionsDetails?.id && positionsDetails?.hasGtt && positionsDetails?.productTag !== "INTRADAY") ||
                (holdingDetails?.finCode && holdingDetails?.hasGtt)) && (
                <>
                    <div
                        onClick={() => {
                            dispatch(resetStocksDetails());
                            router.push(
                                `/user/orders/gtt?symbol=${
                                    positionsDetails?.titleWithSubtitle ??
                                    holdingDetails?.titleWithSubtitle ??
                                    holdingDetails?.titleWithSubtitle
                                }`
                            );
                            Mixpanel.track("cta_clicked", {
                                cta_text: "view_gtt",
                                page: "positions_bf",
                                tab: "positions",
                                symbol: positionsDetails?.symbol,
                                instrument_name: getInstrument(positionsDetails?.symbol),
                                is_position_open: positionsDetails?.open ? "yes" : "no",
                                is_gtt: positionsDetails?.hasGtt ? "yes" : "no",
                                is_tradeidea: positionsDetails?.tradeCardId ? "yes" : "no",
                                expiry_date: null,
                                product: positionsDetails?.productTag,
                                is_gtt: true
                            });
                        }}
                        className="cursor-pointer mt-4 mx-auto h-fit self-center w-fit flex items-center gap-2 border border-[#0862BC] px-[12px] rounded-[20px] py-[1px] border-solid"
                    >
                        <p className="text-[#0862BC] text-center text-xs font-bold">
                            View active <span className='[font-family:"Kaushan_Script"] '>GTT</span>
                        </p>
                        <Image className="w-[12px]" src={gttIcon} alt="gttIcon" />
                    </div>
                </>
            )}

            {/* holding details as table */}
            {holdingDetails?.finCode && (
                <div className={`transition-all overflow-hidden pt-4 `}>
                    <div className="flex justify-between items-center bg-[#F5F5F5] p-2 rounded-md text-black text-xs leading-5">
                        <p className="font-medium text-[#414141]">Qty.</p>
                        <p className="font-bold">{putComma(holdingDetails?.quantity, 0)}</p>
                    </div>
                    <div className="flex justify-between items-center p-2 rounded-md text-black text-xs leading-5">
                        <p className="font-medium text-[#414141]">Avg. price</p>
                        <p className="font-bold">₹{putComma(holdingDetails?.cost_price)}</p>
                    </div>
                    <div className="flex justify-between items-center bg-[#F5F5F5] p-2 rounded-md text-black text-xs leading-5">
                        <p className="font-medium text-[#414141]">Day’s P/L</p>
                        <p className="font-bold">
                            <ProfitOrLoss
                                nseLtpPrice={trade?.ltp}
                                nseClosePrice={holdingDetails?.closeP}
                                quantity={holdingDetails?.quantity}
                                textClass="text-xs font-bold leading-4"
                            />
                        </p>
                    </div>
                </div>
            )}
            {((positionsDetails?.id && !positionsDetails?.hasGtt) ||
                (holdingDetails?.finCode && !holdingDetails?.hasGtt)) && (
                <>
                    <div
                        onClick={() => {
                            dispatch(resetStocksDetails());
                            dispatch(
                                setOpenGTTBfSlice({
                                    isOpen: true,
                                    scriptCode:
                                        positionsDetails?.security_id ??
                                        holdingDetails?.nse_security_id ??
                                        holdingDetails?.bse_security_id,
                                    totalQty: positionsDetails?.qty ?? holdingDetails?.quantity,
                                    orderId: positionsDetails?.orderId ?? holdingDetails?.finCode,
                                    hasGtt: positionsDetails?.hasGtt ?? holdingDetails?.hasGtt,
                                    exchange: positionsDetails?.exchange ?? holdingDetails?.primaryExchange,
                                    symbol:
                                        positionsDetails?.symbol ??
                                        holdingDetails?.nse_symbol ??
                                        holdingDetails?.bse_symbol,
                                    segment: positionsDetails?.segment ?? "E",
                                    txnType: positionsDetails?.txnType === "S" ? "B" : "S",
                                    product: positionsDetails?.productType ?? "C",
                                    exch_id: positionsDetails?.exchange ?? holdingDetails?.primaryExchange
                                })
                            );
                            Mixpanel.track("cta_clicked", {
                                cta_text: "set_gtt",
                                page: "positions_bf",
                                tab: "positions",
                                symbol:
                                    positionsDetails?.symbol ??
                                    holdingDetails?.nse_symbol ??
                                    holdingDetails?.bse_symbol,
                                instrument_name: getInstrument(
                                    positionsDetails?.symbol ?? holdingDetails?.nse_symbol ?? holdingDetails?.bse_symbol
                                ),
                                is_position_open: positionsDetails?.open ? "yes" : "no",
                                is_gtt: positionsDetails?.hasGtt ? "yes" : "no",
                                is_tradeidea: positionsDetails?.tradeCardId ? "yes" : "no",
                                expiry_date: null,
                                product: positionsDetails?.productTag
                            });
                        }}
                        className="cursor-pointer my-3 mx-auto h-fit self-center w-fit flex items-center gap-2 border border-[#0862BC] bg-[#0862BC] px-[12px] rounded-[20px] py-[1px] border-solid"
                    >
                        <p className="text-white text-center text-xs font-bold">
                            Set <span className='[font-family:"Kaushan_Script"] '>GTT</span>
                        </p>
                        <Image className="w-[12px]" src={event_white} alt="demo" />
                    </div>
                </>
            )}

            {/* positions details as table */}
            {positionsDetails?.id && (
                <div className={`transition-all overflow-hidden pt-4 ${!isOderDetails ? "max-h-[600px]" : "max-h-0"}`}>
                    <div className="flex justify-between items-center bg-[#F5F5F5] p-2 rounded-md text-black text-xs leading-5">
                        <p className="font-medium text-[#414141]">Avg. price</p>
                        <p className="font-bold">₹{putComma(positionsDetails?.average)}</p>
                    </div>
                    <div className="flex justify-between items-center p-2 rounded-md text-black text-xs leading-5">
                        <p className="font-medium text-[#414141]">Carry forward qty.</p>
                        <p className="font-bold">{putComma(positionsDetails?.tot_buy_qty_cf, 0)}</p>
                    </div>
                </div>
            )}
        </div>
    );
}
